<div class="mx-auto max-w-3xl px-4 py-8">
  <h1 class="font-display mb-6 text-2xl font-bold text-slate-900">My Profile</h1>

  @if (userProfile.loading()) {
    <div class="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-slate-600">
      Loading profile...
    </div>
  } @else if (userProfile.error()) {
    <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
      {{ userProfile.error() }}
    </div>
  } @else if (userProfile.profile(); as profile) {
    <div class="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden relative">
      
      <div class="flex items-center justify-between border-b border-slate-100 bg-white p-6">
        <h2 class="text-lg font-semibold text-slate-800">Personal Information</h2>
        
        @if (!isEditing()) {
          <button (click)="startEdit(profile)" class="p-2 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 rounded-full transition-colors" title="Edit Profile">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
          </button>
        } @else {
          <div class="flex gap-2">
            <button type="button" (click)="cancelEdit()" class="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg> Cancel
            </button>
            <button type="button" (click)="saveEdit()" [disabled]="profileForm.invalid" class="flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg> Save
            </button>
          </div>
        }
      </div>

      @if (!isEditing()) {
        <dl class="grid gap-y-6 gap-x-8 p-6 sm:grid-cols-2">
          <div>
            <dt class="text-sm font-medium text-slate-500">Email</dt>
            <dd class="mt-1 text-slate-900 font-medium">{{ profile.email }}</dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-slate-500">Joined Since</dt>
            <dd class="mt-1 text-slate-900 font-medium">{{ profile.createdAt ? (profile.createdAt | date:'mediumDate') : '—' }}</dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-slate-500">Display name</dt>
            <dd class="mt-1 text-slate-900 font-medium">{{ profile.displayName ?? '—' }}</dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-slate-500">Subscription</dt>
            <dd class="mt-1 capitalize text-slate-900 font-medium">
              {{ profile.subscriptionTier }}
              @if (profile.subscriptionTier !== 'free') {
                <span class="ml-2 text-sm font-normal text-slate-500">
                  Start: {{ profile.subscriptionStartDate ?? '—' }}
                  &nbsp;&nbsp;End: {{ profile.subscriptionEndDate ?? '—' }}
                </span>
              }
            </dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-slate-500">Exam Date</dt>
            <dd class="mt-1 text-slate-900 font-medium">{{ profile.examDate ?? '—' }}</dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-slate-500">My Goal (10–20)</dt>
            <dd class="mt-1 text-slate-900 font-medium">{{ profile.goal ?? '—' }}</dd>
          </div>          
        </dl>
      } 
      @else {
        <form [formGroup]="profileForm" (ngSubmit)="saveEdit()" class="grid gap-y-6 gap-x-8 p-6 sm:grid-cols-2">
          <div class="flex flex-col">
            <label class="mb-1 text-sm font-medium text-slate-500">Email</label>
            <input type="email" [value]="profile.email" disabled class="cursor-not-allowed rounded-md border border-slate-200 bg-slate-50 p-2 text-slate-500">
          </div>

          <div class="flex flex-col">
            <label class="mb-1 text-sm font-medium text-slate-500">Joined Since</label>
            <input type="text" [value]="profile.createdAt ? (profile.createdAt | date:'mediumDate') : ''" disabled class="cursor-not-allowed rounded-md border border-slate-200 bg-slate-50 p-2 text-slate-500">
          </div>

          <div class="flex flex-col">
            <label class="mb-1 text-sm font-medium text-slate-500">Display name</label>
            <input type="text" formControlName="displayName" class="rounded-md border border-slate-300 p-2 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500">
          </div>

          <div class="flex flex-col">
            <label class="mb-1 text-sm font-medium text-slate-500">Subscription</label>
            <div class="cursor-not-allowed rounded-md border border-slate-200 bg-slate-50 p-2 text-slate-500">
              <span class="capitalize font-medium">{{ profile.subscriptionTier }}</span>
              @if (profile.subscriptionTier !== 'free') {
                <span class="ml-2 text-sm text-slate-400">
                  Start: {{ profile.subscriptionStartDate ?? '—' }}
                  &nbsp;&nbsp;End: {{ profile.subscriptionEndDate ?? '—' }}
                </span>
              }
            </div>
          </div>

          <div class="flex flex-col">
            <label class="mb-1 text-sm font-medium text-slate-500">Exam Date</label>
            <input type="date" formControlName="examDate" class="rounded-md border border-slate-300 p-2 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500">
          </div>

          <div class="flex flex-col">
            <label class="mb-1 text-sm font-medium text-slate-500">My Goal (10–20)</label>
            <select formControlName="goal" class="rounded-md border border-slate-300 bg-white p-2 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500">
              @for (n of goalOptions; track n) {
                <option [ngValue]="n">{{ n }}</option>
              }
            </select>
          </div>
          
        </form>
      }
    </div>
  }
</div>